import {PIXI} from '@colonist/pixi'
import {UIGameRankedEndController} from './UIGameRankedEndController'
import {GameColor} from '../../GameColor'
import {TextView} from '../../ui_canvas_engine/TextView'
import {GameFont} from '../../GameFont'
import {RankedProgressBarView} from '../../ui_canvas_engine/RankedProgressBarView'
import {RankedDivisionIconView} from '../../ui_canvas_engine/RankedDivisionIconView'
import {RankedDivisionState, RankedDivisionTypes, RankedMMRState, RankedSubDivisionTypes} from '@colonist/leaderboard-types'
import {BaseEndGameRankedChangeView} from './BaseEndGameRankedChangeView'
import {UIImagePathController} from '../../UIEnums.ts'
import {OpacityAnimationWrapper} from '../ui_game_animations/OpacityAnimationWrapper'
import {RankedProgressBarAnimationWrapper} from '../ui_game_animations/RankedProgressBarAnimationWrapper'
import {PositionAnimationWrapper} from '../ui_game_animations/PositionAnimationWrapper'
import {UIGameEndFooterController} from './UIGameEndFooterController'
import {RankedUtils} from '../../../../../shared/utils/RankedUtils'
import {TranslationObject} from '@colonist/i18n'
import {Sprites} from '../UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'

const rankedProgressBarBorderSize = 3

export class GameEndMMRChangeView extends BaseEndGameRankedChangeView {
    private readonly rankedState: RankedMMRState
    private readonly footerController: UIGameEndFooterController

    private divisionIconWidth: number
    private divisionIconHeight: number
    private divisionIconY: number

    private finalPointTextView: TextView
    private plusMinusPointTextView: TextView
    private promotionDemotionTextView: TextView

    private progressBar: RankedProgressBarView
    private currentDivisionImage: RankedDivisionIconView
    private promotionDivisionImage: RankedDivisionIconView
    private demotionDivisionImage: RankedDivisionIconView

    constructor(uiGameRankedEndController: UIGameRankedEndController, rankedState: RankedMMRState, footerController: UIGameEndFooterController) {
        super(uiGameRankedEndController)
        this.rankedState = rankedState
        this.footerController = footerController
        this.createPromotionDemotionText()
        this.createMMRChangeText()
        this.createEndScreenUpdateText()

        this.createRankedProgressBar()

        this.calculateDivisionImagePositions()

        this.createDemotionDivisionImage(this.rankedState.previousDivisionState.demotionDivisionBoundary.division, this.rankedState.previousDivisionState.demotionDivisionBoundary.subdivision)
        this.createPromotionDivisionImage(this.rankedState.previousDivisionState.promotionDivisionBoundary.division, this.rankedState.previousDivisionState.promotionDivisionBoundary.subdivision)
        this.createCurrentDivisionImage(this.rankedState.previousDivisionState.divisionType, this.rankedState.previousDivisionState.subdivisionType)

        this.phase1AnimatePreviousMMRBoundaries()
    }

    private phase1AnimatePreviousMMRBoundaries() {
        const startPercentage = this.getPreviousMMRProgressBarPercentage()
        let finishPercentage = this.getCurrentMMRProgressBarPercentage()
        if(this.gotPromoted) finishPercentage = 1
        if(this.gotDemoted) finishPercentage = 0

        this.animatePositionOfCurrentDivisionIcon(startPercentage, finishPercentage, this.animationDuration)
        this.footerController.setVisible(false)

        setTimeout(() => this.phase2FadeOutMMRIcons(), this.animationDuration + this.delayBetweenAnimationPhases)
    }

    private phase2FadeOutMMRIcons() {
        if(!this.gotPromotedOrDemoted) {
            this.completedAllAnimationPhases()
            return
        }

        this.animateOpacityOfCurrentDivisionIcon(1, 0, this.animationDuration)

        new OpacityAnimationWrapper(this.promotionDemotionTextView, this.uiGameRankedEndController.uiGameManager, 0, 1, this.animationDuration)

        setTimeout(() => this.phase3UpdateAndFadeInMMRIconsAndAnimateNewMMRBoundaries(), this.animationDuration + this.delayBetweenAnimationPhases)
    }

    private phase3UpdateAndFadeInMMRIconsAndAnimateNewMMRBoundaries() {
        this.contentContainer.removeChild(this.promotionDivisionImage)
        this.contentContainer.removeChild(this.demotionDivisionImage)
        this.contentContainer.removeChild(this.currentDivisionImage)
        this.promotionDivisionImage.destroy()
        this.demotionDivisionImage.destroy()
        this.currentDivisionImage.destroy()
        this.createPromotionDivisionImage(this.rankedState.currentDivisionState.promotionDivisionBoundary.division, this.rankedState.currentDivisionState.promotionDivisionBoundary.subdivision)
        this.createDemotionDivisionImage(this.rankedState.currentDivisionState.demotionDivisionBoundary.division, this.rankedState.currentDivisionState.demotionDivisionBoundary.subdivision)
        this.createCurrentDivisionImage(this.rankedState.currentDivisionState.divisionType, this.rankedState.currentDivisionState.subdivisionType)

        this.progressBar.percentage = this.gotPromoted ? 0 : 1

        const startPercentage = this.progressBar.percentage
        const finishPercentage = this.getCurrentMMRProgressBarPercentage()
        const moveProgressBarTimeInMs = this.lostMMR ? 200 : 1000
        this.animatePositionOfCurrentDivisionIcon(startPercentage, finishPercentage, moveProgressBarTimeInMs, this.animationDuration)
        this.animateOpacityOfCurrentDivisionIcon(0, 1, this.animationDuration)

        setTimeout(() => this.completedAllAnimationPhases(), this.animationDuration + this.delayBetweenAnimationPhases + moveProgressBarTimeInMs)
    }

    get gotPromotedOrDemoted(): boolean {
        if(RankedUtils.isFirstRankedStateHigherThan(this.rankedState.currentDivisionState, this.rankedState.previousDivisionState)) return true
        if(RankedUtils.isFirstRankedStateHigherThan(this.rankedState.previousDivisionState, this.rankedState.currentDivisionState)) return true

        return false
    }

    get gotPromoted(): boolean {
        return RankedUtils.isFirstRankedStateHigherThan(this.rankedState.currentDivisionState, this.rankedState.previousDivisionState)
    }

    get gotDemoted(): boolean {
        return RankedUtils.isFirstRankedStateHigherThan(this.rankedState.previousDivisionState, this.rankedState.currentDivisionState)
    }

    private get lostMMR(): boolean {
        return this.rankedState.currentMMR < this.rankedState.previousMMR
    }

    private get animationDuration(): number {
        const animationDurationIfWon = 2000
        const animationDurationIfLost = 200
        return this.lostMMR ? animationDurationIfLost : animationDurationIfWon
    }

    private get delayBetweenAnimationPhases(): number {
        const delayBetweenPhasesIfWon = 1000
        const delayBetweenPhasesIfLost = 100
        return this.lostMMR ? delayBetweenPhasesIfLost : delayBetweenPhasesIfWon
    }

    private completedAllAnimationPhases() {
        this.footerController.setVisible(true)
        this.footerController.fadeInButtons(1500)
    }

    private animatePositionOfCurrentDivisionIcon(startPercentage: number, finishPercentage: number, duration: number, initialDelayInMs: number = 0) {
        const startPosX = this.percentageToImagePositionX(startPercentage)
        const finishPosX = this.percentageToImagePositionX(finishPercentage)
        new PositionAnimationWrapper(this.currentDivisionImage, 'x', this.uiGameRankedEndController.uiGameManager, startPosX, finishPosX, duration, initialDelayInMs)
        new RankedProgressBarAnimationWrapper(this.progressBar, this.uiGameRankedEndController.uiGameManager, startPercentage, finishPercentage, duration, initialDelayInMs)
    }

    private animateOpacityOfCurrentDivisionIcon(startOpacity: number, finishOpacity: number, duration: number) {
        new OpacityAnimationWrapper(this.currentDivisionImage, this.uiGameRankedEndController.uiGameManager, startOpacity, finishOpacity, duration)
    }

    private getCurrentMMRProgressBarPercentage(): number {
        return getRankedProgressPercentage(this.rankedState.currentMMR, this.rankedState.currentDivisionState)
    }

    private getPreviousMMRProgressBarPercentage(): number {
        const previousMMR = this.rankedState.currentMMR - this.rankedState.mostRecentChangeInValue
        return getRankedProgressPercentage(previousMMR, this.rankedState.previousDivisionState)
    }

    private createPromotionDemotionText() {
        const text = this.rankedState.promotionDemotionText
        const textPositionY = this.sectionSize * 2

        this.promotionDemotionTextView = new TextView(text, 0, textPositionY, this.contentContainer.width, this.sectionSize, this.contentContainer, GameFont.Size.XXL, GameColor.getColor(GameColor.Color.Gray3))
        this.promotionDemotionTextView.centerXInParent()
        this.promotionDemotionTextView.alpha = 0
    }

    private createMMRChangeText() {
        const text = this.rankedState.getEndGameScreenMMRChangeText()
        const textPositionY = this.sectionSize * 2.5
        const textColor = this.rankedState.mostRecentChangeInValue > 0 ? GameColor.getColor(GameColor.Color.GreenDark) : GameColor.getColor(GameColor.Color.RedDark)

        this.plusMinusPointTextView = new TextView(text, 0, textPositionY, this.contentContainer.width, this.sectionSize, this.contentContainer, GameFont.Size.XXL, textColor, true)
        this.plusMinusPointTextView.centerXInParent()
        new OpacityAnimationWrapper(this.plusMinusPointTextView, this.uiGameRankedEndController.uiGameManager, 0, 1, 300, this.animationDuration)
    }

    private createEndScreenUpdateText() {
        const text = this.rankedState.getEndGameScreenUpdateText()
        const textPositionY = this.sectionSize * 2.5 + this.plusMinusPointTextView.height

        this.finalPointTextView = new TextView(text, 0, textPositionY, this.contentContainer.width, this.sectionSize, this.contentContainer, GameFont.Size.XXL, GameColor.getColor(GameColor.Color.Gray3), true)
        this.finalPointTextView.centerXInParent()
    }

    private createRankedProgressBar() {
        const isNewMobileUI = UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI
        const rankedProgressBarWidth = isNewMobileUI ? this.uiGameRankedEndController.uiGameManager.canvasController.appW * 0.75 : this.sectionSize * 4
        const rankedProgressBarHeight = isNewMobileUI ? this.contentContainer.height / 45 : this.contentContainer.height / 33
        const scaledBorderWidth = rankedProgressBarBorderSize / this.uiGameRankedEndController.uiGameManager.canvasController.responsiveController.getScaleForRotation()

        this.progressBar = new RankedProgressBarView(rankedProgressBarWidth, rankedProgressBarHeight, this.contentContainer, scaledBorderWidth)
        this.progressBar.fillColor = GameColor.getColorHexValue(GameColor.Color.GreenDark)
        this.progressBar.emptyColor = GameColor.getColorHexValue(GameColor.Color.White)
        this.progressBar.borderColor = GameColor.getColorHexValue(GameColor.Color.White)
        this.progressBar.x = this.contentContainer.width / 2 - this.progressBar.width / 2
        this.progressBar.y = this.sectionSize * 4
    }

    private calculateDivisionImagePositions() {
        const iconWidth = this.contentContainer.width / 19
        this.divisionIconWidth = iconWidth + (this.contentContainer.width / 21)
        this.divisionIconHeight = iconWidth + (this.contentContainer.height / 78.8)
        this.divisionIconY = this.progressBar.y + (this.progressBar.height / 2) - (this.divisionIconHeight / 2) - rankedProgressBarBorderSize
    }

    private percentageToImagePositionX(percentage: number) {
        const minX = this.progressBar.x - (this.divisionIconWidth / 2)
        const maxX = minX + this.progressBar.width
        const range = maxX - minX
        return minX + (range * percentage)
    }

    private createDemotionDivisionImage(division: RankedDivisionTypes, subdivision: RankedSubDivisionTypes) {
        const divisionTexture = division != RankedDivisionTypes.None ? getTextureForDivision(division) : PIXI.Texture.EMPTY
        const subdivisionTexture = getTextureForSubdivision(subdivision)
        this.demotionDivisionImage = this.createDivisionImage(0, divisionTexture, subdivisionTexture, {key: 'strings:colonistCommon.utils.emptyString'})
        const demotionDivisionImageX = this.progressBar.x - this.demotionDivisionImage.width
        this.demotionDivisionImage.x = demotionDivisionImageX
    }

    private createPromotionDivisionImage(division: RankedDivisionTypes, subdivision: RankedSubDivisionTypes) {
        const divisionTexture = division != RankedDivisionTypes.None ? getTextureForDivision(division) : UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.white_number_one)
        const subdivisionTexture = getTextureForSubdivision(subdivision)
        this.promotionDivisionImage = this.createDivisionImage(0, divisionTexture, subdivisionTexture, {key: 'strings:colonistCommon.utils.emptyString'})
        const promotionDivisionImageX = this.progressBar.x + this.progressBar.width
        this.promotionDivisionImage.x = promotionDivisionImageX
    }

    private createCurrentDivisionImage(division: RankedDivisionTypes, subdivision: RankedSubDivisionTypes) {
        const divisionTexture = getTextureForDivision(division)

        const subdivisionTexture = getTextureForSubdivision(subdivision)
        this.currentDivisionImage = this.createDivisionImage(0, divisionTexture, subdivisionTexture, {key: 'strings:colonistCommon.utils.#buffer', options: {value: this.rankedState.currentMMR}})
    }

    private createDivisionImage(iconPositionX: number, divisionTexture: PIXI.Texture, subdivisionTexture: PIXI.Texture, topTextValue: TranslationObject) {
        const divisionIcon = new RankedDivisionIconView(iconPositionX, this.divisionIconY, this.divisionIconWidth, this.divisionIconHeight, this.contentContainer, divisionTexture, subdivisionTexture, topTextValue)
        return divisionIcon
    }

    hide() {
        this.plusMinusPointTextView?.hide()
        this.promotionDemotionTextView?.hide()
        this.finalPointTextView?.hide()
        this.contentContainer.removeChild(this.progressBar)
        this.contentContainer.removeChild(this.currentDivisionImage)
        this.contentContainer.removeChild(this.demotionDivisionImage)
        this.contentContainer.removeChild(this.promotionDivisionImage)
    }
}

function getRankedProgressPercentage(mmrPoints: number, divisionState: RankedDivisionState): number {
    const lowerMMRBound = divisionState.demotionDivisionBoundary.boundary
    const higherMMRBound = divisionState.promotionDivisionBoundary.boundary
    const percentage = (mmrPoints - lowerMMRBound) / (higherMMRBound - lowerMMRBound)
    return percentage
}

function getTextureForDivision(division: RankedDivisionTypes): PIXI.Texture {
    const image = UIImagePathController.getRankedDivisionImage(division)
    return UIGameSpriteHandler.getTextureForLobbyImage(image)
}

function getTextureForSubdivision(division: RankedSubDivisionTypes): PIXI.Texture {
    const lobbyImage = UIImagePathController.getRankedSubdivisionImage(division)
    if(lobbyImage == undefined) return PIXI.Texture.EMPTY

    return UIGameSpriteHandler.getTextureForLobbyImage(lobbyImage)
}
